import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { membershipApplicationAPI } from '../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from '../../components/UI/ConfirmDialog';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #555;
  border-bottom: 2px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Label = styled.div`
  font-weight: 600;
  color: #666;
`;

const Value = styled.div`
  color: #333;
`;

const LinkButton = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'submitted':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'under_review':
        return 'background: #fff3e0; color: #f57c00;';
      case 'committee_review':
        return 'background: #fce4ec; color: #c2185b;';
      case 'eligible':
        return 'background: #f3e5f5; color: #7b1fa2;';
      case 'resubmit':
        return 'background: #fff8e1; color: #f9a825;';
      case 'approved':
        return 'background: #e8f5e9; color: #388e3c;';
      case 'rejected':
        return 'background: #ffebee; color: #d32f2f;';
      default:
        return 'background: #f5f5f5; color: #666;';
    }
  }}
`;

const Button = styled.button`
  background: ${({ theme, variant }) => {
    switch (variant) {
      case 'danger':
        return '#dc3545';
      case 'warning':
        return '#f9a825';
      case 'secondary':
        return '#6c757d';
      default:
        return theme.colors.primary;
    }
  }};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [committeeNotes, setCommitteeNotes] = useState('');
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    title: '',
    message: ''
  });

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await membershipApplicationAPI.getSubmission(id);
      setSubmission(response.data);
      setCommitteeNotes(response.data.committee_notes || '');
    } catch (error) {
      console.error('Error fetching submission:', error);
      toast.error('Failed to load submission');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (type) => {
    if (type === 'reject' && !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setActionDialog({
      open: true,
      type,
      title: type === 'approve' ? 'Approve Application' : 'Reject Application',
      message: type === 'approve'
        ? 'Are you sure you want to approve this application? The applicant will become an active member immediately.'
        : 'Are you sure you want to reject this application?',
    });
  };

  const closeActionDialog = () => {
    if (processing) return;
    setActionDialog({
      open: false,
      type: null,
      title: '',
      message: '',
    });
  };

  const approveSubmission = async () => {
    setProcessing(true);
    try {
      const response = await membershipApplicationAPI.approveSubmission(id);
      toast.success(response.data.message);

      if (response.data.member) {
        toast.info(`Member created: ${response.data.member.member_id}`);
      }

      navigate('/app/members');
    } catch (error) {
      console.error('Error approving:', error);
      toast.error(error.response?.data?.message || 'Failed to approve application');
    } finally {
      setProcessing(false);
      closeActionDialog();
    }
  };

  const rejectSubmission = async () => {
    setProcessing(true);
    try {
      await membershipApplicationAPI.rejectSubmission(id, rejectReason);
      toast.success('Application rejected');
      navigate('/app/membership-applications/submissions');
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error(error.response?.data?.message || 'Failed to reject application');
    } finally {
      setProcessing(false);
      closeActionDialog();
    }
  };

  const handleActionConfirm = async () => {
    if (actionDialog.type === 'approve') {
      await approveSubmission();
    } else if (actionDialog.type === 'reject') {
      await rejectSubmission();
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === 'resubmit' && !committeeNotes.trim()) {
      toast.error('Please provide committee notes before requesting a resubmission.');
      return;
    }

    setProcessing(true);
    try {
      await membershipApplicationAPI.updateSubmissionStatus(id, { 
        status: newStatus,
        committee_notes: committeeNotes
      });
      toast.success('Status updated successfully');
      fetchSubmission();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const formatStatus = (status) => {
    if (status === 'resubmit') {
      return 'Needs Resubmission';
    }
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

const RAW_API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
const API_ORIGIN = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;
const API_BASE_URL = API_ORIGIN;
const MEDIA_ENDPOINT = `${API_ORIGIN}/api/media`;

const resolveFileUrl = (input) => {
  if (!input) return null;
  const path = String(input);

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalize = (value) => {
    let normalized = value.replace(/\\/g, '/');

    if (normalized.startsWith('/storage/')) {
      normalized = normalized.replace(/^\/storage\//, '');
    } else if (normalized.startsWith('storage/')) {
      normalized = normalized.replace(/^storage\//, '');
    }

    if (normalized.startsWith('public/')) {
      normalized = normalized.replace(/^public\//, '');
    }

    return normalized;
  };

  if (path.startsWith('/')) {
    if (path.startsWith('/storage/')) {
      const cleaned = normalize(path);
      return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
    }

    return `${API_BASE_URL}${path}`;
  }

  const cleaned = normalize(path);
  return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
};

  const {
    accessibility,
    supportingDocs,
  } = useMemo(() => {
    const formData = submission?.form_data || {};
    const files = submission?.files || {};

    const normalizeBoolean = (value) => {
      if (typeof value === 'boolean') return value;
      if (value === undefined || value === null) return null;
      const normalized = String(value).toLowerCase();
      if (['1', 'yes', 'true', 'y'].includes(normalized)) return true;
      if (['0', 'no', 'false', 'n'].includes(normalized)) return false;
      return null;
    };

    const disabilityStatus = normalizeBoolean(
      formData.has_disability ??
      formData.physical_disability ??
      formData.disability_status
    );

    let disabilityTypes = [];
    const typesValue = formData.disability_types ?? formData.disability_type ?? [];
    if (Array.isArray(typesValue)) {
      disabilityTypes = typesValue.filter(Boolean);
    } else if (typeof typesValue === 'string' && typesValue.trim().length) {
      disabilityTypes = typesValue.split(',').map(item => item.trim()).filter(Boolean);
    }

    const disabilityOther =
      formData.disability_other ??
      formData.disability_details ??
      '';

    const cvPath =
      files.cv ||
      formData.cv ||
      formData.resume ||
      formData.cv_url ||
      null;

    const cvUrl = cvPath ? resolveFileUrl(cvPath) : null;

    return {
      accessibility: {
        disabilityStatus,
        disabilityTypes,
        disabilityOther: typeof disabilityOther === 'string' ? disabilityOther.trim() : '',
      },
      supportingDocs: {
        cvUrl,
      },
    };
  }, [submission]);

  const fieldLabelMap = useMemo(() => {
    const map = {};
    const combinedFields = [
      ...(submission?.form?.activeFields || []),
      ...(submission?.form?.active_fields || []),
      ...(submission?.form?.fields || []),
      ...(submission?.form?.form_fields || []),
    ];

    combinedFields.forEach((field) => {
      const name = field?.field_name;
      if (!name) return;
      const label = field?.field_label || field?.label || field?.placeholder || name;
      map[name] = label;
    });

    const labelMaps = [
      submission?.form?.field_label_map,
      submission?.form?.fieldLabels,
      submission?.form?.field_labels,
    ];

    labelMaps.forEach((labelMap) => {
      if (!labelMap || typeof labelMap !== 'object') return;
      Object.entries(labelMap).forEach(([key, label]) => {
        if (key && label && !map[key]) {
          map[key] = label;
        }
      });
    });

    return map;
  }, [submission]);

  if (loading) {
    return <Container><p>Loading...</p></Container>;
  }

  if (!submission) {
    return <Container><p>Submission not found</p></Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Application Details</Title>
        <Button variant="secondary" onClick={() => navigate('/app/membership-applications/submissions')}>
          ← Back to List
        </Button>
      </Header>

      <Card>
        <Section>
          <SectionTitle>Application Information</SectionTitle>
          <InfoGrid>
            <Label>Application ID:</Label>
            <Value>#{submission.id}</Value>
            
            <Label>Status:</Label>
            <Value>
              <StatusBadge status={submission.status}>
                {formatStatus(submission.status)}
              </StatusBadge>
            </Value>
            
            <Label>Submitted:</Label>
            <Value>{new Date(submission.submitted_at || submission.created_at).toLocaleString()}</Value>
            
            {submission.approved_at && (
              <>
                <Label>Approved:</Label>
                <Value>{new Date(submission.approved_at).toLocaleString()}</Value>
              </>
            )}
            
            {submission.rejection_reason && (
              <>
                <Label>Rejection Reason:</Label>
                <Value style={{ color: '#d32f2f' }}>{submission.rejection_reason}</Value>
              </>
            )}
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>Applicant Information</SectionTitle>
          <InfoGrid>
            <Label>Full Name:</Label>
            <Value>{submission.applicant_name || 'N/A'}</Value>
            
            <Label>Email:</Label>
            <Value>{submission.applicant_email || 'N/A'}</Value>
            
            <Label>Phone:</Label>
            <Value>{submission.form_data?.phone_number || submission.applicant_phone || 'N/A'}</Value>
            
            {submission.form_data?.gender && (
              <>
                <Label>Gender:</Label>
                <Value>{submission.form_data.gender}</Value>
              </>
            )}
            
            {submission.form_data?.age && (
              <>
                <Label>Age:</Label>
                <Value>{submission.form_data.age}</Value>
              </>
            )}
          </InfoGrid>
        </Section>

        {submission.form_data && (
          <Section>
            <SectionTitle>Application Responses</SectionTitle>
            <InfoGrid>
              {Object.entries(submission.form_data)
                .filter(([key]) => !['cv', 'resume', 'cv_url'].includes(key))
                .map(([key, value]) => {
                  const displayLabel = fieldLabelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <React.Fragment key={key}>
                      <Label>{displayLabel}:</Label>
                      <Value>
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </Value>
                    </React.Fragment>
                  );
                })}
            </InfoGrid>
          </Section>
        )}

        {(accessibility.disabilityStatus !== null || (accessibility.disabilityTypes && accessibility.disabilityTypes.length)) && (
          <Section>
            <SectionTitle>Accessibility Information</SectionTitle>
            <InfoGrid>
              <Label>Identifies as having a physical disability:</Label>
              <Value>
                {accessibility.disabilityStatus === null
                  ? 'Not provided'
                  : accessibility.disabilityStatus
                    ? 'Yes'
                    : 'No'}
              </Value>

              {accessibility.disabilityStatus && (
                <>
                  <Label>Disability Type(s):</Label>
                  <Value>
                    {accessibility.disabilityTypes && accessibility.disabilityTypes.length
                      ? accessibility.disabilityTypes.join(', ')
                      : 'Not specified'}
                  </Value>
                </>
              )}

              {accessibility.disabilityStatus && accessibility.disabilityOther && (
                <>
                  <Label>Additional Details:</Label>
                  <Value>{accessibility.disabilityOther}</Value>
                </>
              )}
            </InfoGrid>
          </Section>
        )}

        {/* ID Card & Documents Section */}
        <Section>
          <SectionTitle>Supporting Documents</SectionTitle>
          <InfoGrid>
            {submission.id_card_path && (
              <>
                <Label>ID Card:</Label>
                <Value>
                  <LinkButton href={resolveFileUrl(submission.id_card_path)} target="_blank" rel="noopener noreferrer">
                    View ID Card
                  </LinkButton>
                </Value>
              </>
            )}
            {supportingDocs.cvUrl && (
              <>
                <Label>Resume / CV:</Label>
                <Value>
                  <LinkButton href={supportingDocs.cvUrl} target="_blank" rel="noopener noreferrer">
                    Download uploaded CV
                  </LinkButton>
                </Value>
              </>
            )}
            {!submission.id_card_path && !supportingDocs.cvUrl && (
              <>
                <Label>Documents:</Label>
                <Value style={{ color: '#999' }}>No documents uploaded</Value>
              </>
            )}
          </InfoGrid>
        </Section>

        {/* Interested Trainings Section */}
        {submission.interested_trainings && submission.interested_trainings.length > 0 && (
          <Section>
            <SectionTitle>Interested Trainings</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {submission.interested_trainings.map((training, idx) => (
                <span key={idx} style={{
                  background: '#e3f2fd',
                  color: '#1976d2',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.875rem'
                }}>
                  {training}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Previous Trainings Section */}
        <Section>
          <SectionTitle>Previous Training History</SectionTitle>
          <InfoGrid>
            <Label>Has Previous Training:</Label>
            <Value>{submission.has_previous_training ? 'Yes' : 'No'}</Value>
          </InfoGrid>
          
          {submission.has_previous_training && submission.previous_trainings && submission.previous_trainings.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {submission.previous_trainings.map((training, idx) => (
                <div key={idx} style={{
                  background: '#f5f5f5',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  borderLeft: '4px solid #1976d2'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>Training #{idx + 1}</h4>
                  <InfoGrid>
                    <Label>Training Name:</Label>
                    <Value>{training.name || 'N/A'}</Value>
                    
                    <Label>Trainer/Institution:</Label>
                    <Value>{training.institution || 'N/A'}</Value>
                    
                    <Label>Training Date:</Label>
                    <Value>{training.date ? new Date(training.date).toLocaleDateString() : 'N/A'}</Value>
                    
                    {training.certificate_path && (
                      <>
                        <Label>Certificate:</Label>
                        <Value>
                          <LinkButton href={resolveFileUrl(training.certificate_path)} target="_blank" rel="noopener noreferrer">
                            View Certificate
                          </LinkButton>
                        </Value>
                      </>
                    )}
                  </InfoGrid>
                </div>
              ))}
            </div>
          )}
          
          {submission.has_previous_training && (!submission.previous_trainings || submission.previous_trainings.length === 0) && (
            <p style={{ color: '#999', marginTop: '0.5rem' }}>No training details provided</p>
          )}
        </Section>

        {submission.status !== 'approved' && submission.status !== 'rejected' && (
          <Section>
            <SectionTitle>Committee Notes</SectionTitle>
            <Textarea
              value={committeeNotes}
              onChange={(e) => setCommitteeNotes(e.target.value)}
              placeholder="Add notes about this application..."
            />
            
            <div style={{ marginTop: '1rem' }}>
              <Button onClick={() => handleUpdateStatus('under_review')} disabled={processing}>
                Mark as Under Review
              </Button>
              <Button onClick={() => handleUpdateStatus('committee_review')} disabled={processing}>
                Send to Committee
              </Button>
              <Button onClick={() => handleUpdateStatus('eligible')} disabled={processing}>
                Mark as Eligible
              </Button>
              <Button
                variant="warning"
                onClick={() => handleUpdateStatus('resubmit')}
                disabled={processing || !committeeNotes.trim()}
              >
                ↺ Request Resubmission
              </Button>
            </div>
          </Section>
        )}

        {submission.status !== 'approved' && submission.status !== 'rejected' && (
          <Section>
            <SectionTitle>Actions</SectionTitle>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Button onClick={() => openActionDialog('approve')} disabled={processing}>
                ✓ Approve
              </Button>
              <Button
                variant="warning"
                onClick={() => handleUpdateStatus('resubmit')}
                disabled={processing || !committeeNotes.trim()}
              >
                ↺ Resubmit
              </Button>
              <Button
                variant="danger"
                onClick={() => openActionDialog('reject')}
                disabled={processing || !rejectReason.trim()}
              >
                ✗ Reject
              </Button>
            </div>
            <div style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
              <p style={{ margin: 0 }}>
                <strong>Approve:</strong> applicant becomes an active member immediately.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Resubmit:</strong> requires a note above and sends the form back to the applicant for updates.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Reject:</strong> permanently rejects the application (add details in the box above).
              </p>
            </div>
          </Section>
        )}

        {submission.status === 'approved' && (
          <Section>
            <div style={{ background: '#e8f5e9', padding: '1rem', borderRadius: '4px', marginTop: '1rem' }}>
              <p style={{ color: '#388e3c', margin: 0 }}>
                ✓ This application has been approved. The applicant is now an <strong>active member</strong> of the network.
              </p>
            </div>
          </Section>
        )}
      </Card>

      <ConfirmDialog
        isOpen={actionDialog.open}
        title={actionDialog.title || 'Confirm Action'}
        message={actionDialog.message}
        confirmLabel={actionDialog.type === 'reject' ? 'Reject' : 'Approve'}
        confirmVariant={actionDialog.type === 'reject' ? 'danger' : 'primary'}
        loading={processing}
        onConfirm={handleActionConfirm}
        onCancel={closeActionDialog}
      />
    </Container>
  );
};

export default SubmissionDetail;



